import { StaderEthxAbi, StaderMaticXAbi } from '../abis/stader'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/**
 * Stader ETHx reader.
 *
 * Two calls: `ETHx.totalSupply()` + `StakePoolsManager
 * .getExchangeRate()` (returns 1e18-scaled ETH-per-ETHx rate).
 *
 * Fallback: when `readerExtras.staderStakePoolsManager` is absent,
 * fall back to `ETHx.totalSupply()` with a 1:1 placeholder rate.
 */
export const readerStaderEthx = (entry: LstRegistryEntry): LstReader => {
  const manager = entry.readerExtras?.staderStakePoolsManager
  if (!manager) {
    return {
      calls: [{ address: entry.address, name: 'totalSupply', params: [] }],
      abis: [TotalSupplyAbi],
      parse: ([supply]) => {
        const totalSupply = toBigInt(supply)
        if (totalSupply === undefined) return undefined
        return {
          totalAssets: totalSupply,
          totalSupply,
          exchangeRate: ONE_E18,
        }
      },
    }
  }
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: manager, name: 'getExchangeRate', params: [] },
    ],
    abis: [TotalSupplyAbi, StaderEthxAbi],
    parse: ([supply, rate]) => {
      const totalSupply = toBigInt(supply)
      const exchangeRate = toBigInt(rate)
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
      }
    },
  }
}

/**
 * Stader MaticX reader.
 *
 * Two calls:
 *  1. `MaticX.totalSupply()` — on the entry's chain (Ethereum
 *     L1 entry or Polygon bridge-token entry)
 *  2. `convertMaticXToMatic(1e18)` — on the **home** contract; the
 *     Polygon bridge child has no rate getter, so the call always
 *     goes to the Ethereum home contract. When
 *     `readerExtras.staderMaticXHomeContract` is set, the call is
 *     dispatched cross-chain to the home contract on
 *     `readerExtras.staderMaticXHomeChainId`. Otherwise it's a
 *     local call on `entry.address`.
 *
 * The 3-tuple return is `(amountInMatic, totalShares,
 * totalPooledMatic)`; element 0 is the 1e18-scaled rate, element 2
 * is total MATIC pooled (a stronger `totalAssets` than the derived
 * `totalSupply * rate / 1e18`, but only from the home chain — on
 * Polygon we still surface the Polygon-side `totalSupply`).
 */
export const readerStaderMaticX = (entry: LstRegistryEntry): LstReader => {
  const homeContract = entry.readerExtras?.staderMaticXHomeContract
  const homeChainId = entry.readerExtras?.staderMaticXHomeChainId
  const rateAddress = homeContract ?? entry.address
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      {
        address: rateAddress,
        name: 'convertMaticXToMatic',
        params: [ONE_E18],
        chainId: homeChainId,
      },
    ],
    abis: [TotalSupplyAbi, StaderMaticXAbi],
    parse: ([supply, tup]) => {
      const totalSupply = toBigInt(supply)
      if (!Array.isArray(tup) || tup.length < 3) return undefined
      const amountInMatic = toBigInt(tup[0])
      const totalPooledMatic = toBigInt(tup[2])
      if (totalSupply === undefined || amountInMatic === undefined) {
        return undefined
      }
      // For the L1 entry, prefer the home contract's totalPooledMatic
      // (consistent reporting snapshot). For a cross-chain Polygon
      // entry we surface the *Polygon* totalSupply but still derive
      // totalAssets from the rate — Polygon-side circulating supply
      // × home-chain rate, an approximation of the Polygon TVL.
      const isCrossChain = homeContract !== undefined
      return {
        totalAssets: isCrossChain
          ? (totalSupply * amountInMatic) / ONE_E18
          : (totalPooledMatic ?? (totalSupply * amountInMatic) / ONE_E18),
        totalSupply,
        exchangeRate: amountInMatic,
      }
    },
  }
}
